import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Info, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { FormInput, FormPasswordInput, SubmitButton } from '@/components/form'
import { usersService } from '@/services/users'
import { getApiErrorMessage } from '@/utils/errors'
import { IndividualType, RppsFunction, PASSWORD_VALIDATION } from '@/constants/account'
import { OfferType } from '@/constants/wizard'
import { ROUTES } from '@/constants/routes'

interface AccountCreationStepProps {
  rppsNumber: string
  email?: string
  password?: string
  confirmPassword?: string
  lastName?: string
  firstName?: string
  selectedOffer?: OfferType | null
  selectedStructureId?: string | null
  rppsFunction?: string
  selectedPriceId?: string | null
  finessNumber?: string
  facilityName?: string
  organizationLegalName?: string
  isStructurePlan?: boolean
  isQuoteFlow?: boolean
  isCustomOfferFlow?: boolean
  quoteAddress?: string
  quoteCity?: string
  quotePostalCode?: string
  quotePhone?: string
  desiredLicensesCount?: string
  desiredBillingDetails?: string
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onConfirmPasswordChange: (value: string) => void
  onLastNameChange?: (value: string) => void
  onFirstNameChange?: (value: string) => void
  onNext: () => void
}

export function AccountCreationStep({
  rppsNumber,
  email = '',
  password = '',
  confirmPassword = '',
  lastName = '',
  firstName = '',
  selectedOffer = null,
  selectedStructureId = null,
  rppsFunction = '',
  selectedPriceId = null,
  finessNumber = '',
  facilityName = '',
  organizationLegalName = '',
  isStructurePlan = false,
  isQuoteFlow = false,
  isCustomOfferFlow = false,
  quoteAddress = '',
  quoteCity = '',
  quotePostalCode = '',
  quotePhone = '',
  desiredLicensesCount = '',
  desiredBillingDetails = '',
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onLastNameChange,
  onFirstNameChange,
}: AccountCreationStepProps) {
  const { t } = useTranslation('wizard')
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      setError(t('accountCreation.errors.passwordsMismatch'))
      return
    }

    if (
      password.length < PASSWORD_VALIDATION.MIN_LENGTH ||
      !PASSWORD_VALIDATION.REQUIRE_UPPERCASE.test(password) ||
      !PASSWORD_VALIDATION.REQUIRE_NUMBER.test(password)
    ) {
      setError(t('accountCreation.passwordHint'))
      return
    }

    if (!isCustomOfferFlow && !selectedPriceId) {
      setError(t('accountCreation.errors.noPriceSelected'))
      return
    }

    if (isStructurePlan && (!firstName?.trim() || !lastName?.trim())) {
      setError(t('accountCreation.errors.missingNames'))
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let requestData: Parameters<typeof usersService.createUser>[0]

      if (isCustomOfferFlow && isStructurePlan && (finessNumber || organizationLegalName)) {
        const additionalData: Record<string, unknown> = {}
        if (organizationLegalName) {
          additionalData.organization_data = { name: organizationLegalName.trim() }
        }
        const licensesCount = desiredLicensesCount ? parseInt(desiredLicensesCount, 10) : NaN
        const customPricingData: Record<string, unknown> = {}
        if (!isNaN(licensesCount) && licensesCount >= 1) {
          customPricingData.desired_licenses_count = licensesCount
        }
        if (desiredBillingDetails?.trim()) {
          customPricingData.desired_billing_details = desiredBillingDetails.trim()
        }
        if (Object.keys(customPricingData).length > 0) {
          additionalData.custom_pricing_data = customPricingData
        }
        requestData = {
          ...(finessNumber && { finess_number: finessNumber }),
          email,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          custom_price: true,
          ...(facilityName && { facility_name: facilityName }),
          ...(Object.keys(additionalData).length > 0 && { additional_data: additionalData }),
        }
      } else if (isStructurePlan && (finessNumber || organizationLegalName)) {
        const organizationData: Record<string, unknown> = {}
        if (organizationLegalName) {
          organizationData.name = organizationLegalName.trim()
        }
        if (isQuoteFlow) {
          organizationData.org_address = quoteAddress.trim() || undefined
          organizationData.org_city = quoteCity.trim() || undefined
          organizationData.org_postal_code = quotePostalCode.trim() || undefined
          organizationData.org_phone_number = quotePhone.trim() || undefined
        }
        requestData = {
          ...(finessNumber && { finess_number: finessNumber }),
          email,
          password,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          stripe_subscription_price: selectedPriceId ?? undefined,
          payment_type: isQuoteFlow ? 'external' : 'internal',
          ...(facilityName && { facility_name: facilityName }),
          ...(Object.keys(organizationData).length > 0 && {
            additional_data: { organization_data: organizationData },
          }),
        }
      } else {
        let individualType: IndividualType
        if (selectedOffer === OfferType.MEMBER) {
          individualType = IndividualType.ADHERENT
        } else if (selectedOffer === OfferType.INTERN) {
          individualType =
            rppsFunction?.toLowerCase() === RppsFunction.INTERN
              ? IndividualType.INTERN
              : IndividualType.DOCTOR
        } else {
          individualType = IndividualType.DOCTOR
        }

        requestData = {
          rpps: rppsNumber,
          email,
          password,
          stripe_subscription_price: selectedPriceId ?? undefined,
          individual_type: individualType,
        }

        if (selectedStructureId) {
          requestData.affiliated_org = selectedStructureId
        }
      }

      await usersService.createUser(requestData)

      navigate(ROUTES.ACCOUNT_CREATED, {
        state: isCustomOfferFlow
          ? { isCustomOfferFlow: true }
          : isQuoteFlow
            ? { isQuoteFlow: true }
            : selectedOffer === OfferType.MEMBER
              ? { isMemberFlow: true }
              : undefined,
      })
    } catch (err) {
      setError(getApiErrorMessage(err, t('accountCreation.errors.generic')))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl">
      <div className="mb-10 rounded-2xl border border-[#E9E9E9] bg-white p-6 shadow-[0px_-1px_4px_rgba(0,0,0,0.25)]">
        {isStructurePlan && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border-[0.3px] border-[#B45628] bg-[#FEFCEE] p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#B45628]" />
            <p className="text-sm font-normal text-[#B45628]">
              {isCustomOfferFlow
                ? t('accountCreation.infoBannerCustomOffer')
                : isQuoteFlow
                  ? t('accountCreation.infoBannerQuote')
                  : t('accountCreation.infoBanner')}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm font-normal text-red-600">{error}</p>
          </div>
        )}

        {isStructurePlan && (
          <>
            <FormInput
              id="lastName"
              label={t('accountCreation.lastNameLabel')}
              value={lastName}
              onChange={onLastNameChange ? e => onLastNameChange(e.target.value) : undefined}
              placeholder={t('accountCreation.lastNamePlaceholder')}
              className="mb-3"
              inputClassName="mb-4"
            />
            <FormInput
              id="firstName"
              label={t('accountCreation.firstNameLabel')}
              value={firstName}
              onChange={onFirstNameChange ? e => onFirstNameChange(e.target.value) : undefined}
              placeholder={t('accountCreation.firstNamePlaceholder')}
              className="mb-3"
              inputClassName="mb-4"
            />
          </>
        )}

        <FormInput
          id="email"
          label={t('accountCreation.emailLabel')}
          required
          type="email"
          value={email}
          onChange={e => onEmailChange(e.target.value)}
          placeholder={t('accountCreation.emailPlaceholder')}
          disabled={isLoading}
          className="mb-3"
          inputClassName="mb-4"
        />

        <FormPasswordInput
          id="password"
          label={t('accountCreation.passwordLabel')}
          required
          value={password}
          onChange={e => onPasswordChange(e.target.value)}
          placeholder={t('accountCreation.passwordPlaceholder')}
          disabled={isLoading}
          showPasswordLabel={t('accountCreation.showPassword')}
          hidePasswordLabel={t('accountCreation.hidePassword')}
          hint={t('accountCreation.passwordHint')}
          className="mb-3"
        />

        <FormPasswordInput
          id="confirmPassword"
          label={t('accountCreation.confirmPasswordLabel')}
          required
          value={confirmPassword}
          onChange={e => onConfirmPasswordChange(e.target.value)}
          placeholder={t('accountCreation.confirmPasswordPlaceholder')}
          disabled={isLoading}
          showPasswordLabel={t('accountCreation.showPassword')}
          hidePasswordLabel={t('accountCreation.hidePassword')}
          className="mb-6"
        />

        <SubmitButton isLoading={isLoading} loadingLabel={t('rppsVerification.structureLoading')}>
          {t('accountCreation.submitButton')}
        </SubmitButton>
      </div>
    </form>
  )
}
